import type { ValidationResult, Violation } from '../types'

interface ValidationPanelProps {
  validation: ValidationResult
  onClose: () => void
}

export default function ValidationPanel({ validation, onClose }: ValidationPanelProps) {
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <span className="font-medium">Validation Results</span>
          {validation.valid ? (
            <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
              Valid
            </span>
          ) : (
            <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-full">
              {validation.errors.length} errors
            </span>
          )}
          {validation.warnings.length > 0 && (
            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
              {validation.warnings.length} warnings
            </span>
          )}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-auto p-3 space-y-2">
        {validation.errors.map((v, i) => (
          <ViolationItem key={`error-${i}`} violation={v} />
        ))}
        {validation.warnings.map((v, i) => (
          <ViolationItem key={`warning-${i}`} violation={v} />
        ))}
        {validation.info.map((v, i) => (
          <ViolationItem key={`info-${i}`} violation={v} />
        ))}

        {validation.totalViolations === 0 && (
          <p className="text-sm text-gray-500 text-center py-4">
            No issues found. Your workspace looks good!
          </p>
        )}
      </div>
    </div>
  )
}

function ViolationItem({ violation }: { violation: Violation }) {
  const severityColors = {
    ERROR: 'text-red-600 bg-red-50 border-red-200',
    WARNING: 'text-yellow-600 bg-yellow-50 border-yellow-200',
    INFO: 'text-blue-600 bg-blue-50 border-blue-200',
  }

  const colors = severityColors[violation.severity as keyof typeof severityColors] || severityColors.INFO

  return (
    <div className={`p-2 rounded border ${colors} text-sm`}>
      <div className="flex items-center space-x-2">
        <span className="font-medium text-xs uppercase">{violation.severity}</span>
        <span className="text-gray-500">|</span>
        <span className="text-gray-600">{violation.type}</span>
      </div>
      <p className="mt-1">{violation.message}</p>
    </div>
  )
}
